import { Hexagon, Plus, Trash2 } from "lucide-react";
import { PRICING_TIERS } from "@/components/pricing/PricingContent";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// AgreementDocument — the shared 4-page Pre-Draft Advisory Services Agreement
// renderer. Used by:
//   • the public /agreement template (mode template, editable, localStorage)
//   • the admin contract editor (editable, server-persisted, contract sigs)
//   • the family contract viewer (read-only, contract sigs + e-sign stamps)
//
// Props:
//   form          — the form object (see DEFAULT_FORM)
//   set/setPayment/addPayment/removePayment — edit handlers (no-ops when read-only)
//   readOnly      — when true, all inputs render as static, non-editable text
//   sigMode       — "template" (editable printed-name lines) | "contract"
//                   (renders e-signature stamps / awaiting state from `signatures`)
//   signatures    — { advisor, parent1, parent2, athlete } each {name, signed_at}
// ---------------------------------------------------------------------------

export const DEFAULT_FORM = {
  effectiveDate: "",
  athleteName: "",
  athletePosition: "",
  athleteSchool: "",
  parent1Name: "",
  parent2Name: "",
  familyAddress: "",
  familyCity: "",
  familyState: "TN",
  familyZip: "",
  familyEmail: "",
  familyPhone: "",
  selectedTier: "draft_prep",
  customScope: "",
  customFee: "",
  termStart: "",
  termEnd: "",
  totalFee: "",
  payments: [
    { date: "", amount: "", note: "Engagement deposit, due on execution" },
    { date: "", amount: "", note: "Midpoint installment" },
    { date: "", amount: "", note: "Final installment, due 30 days before NFL Draft" },
  ],
};

const PRINT_CSS = `
  @page { size: letter portrait; margin: 0.6in 0.7in; }
  @media print {
    html, body { background: #ffffff !important; }
    .ag-no-print { display: none !important; }
    .ag-stage { padding: 0 !important; background: #ffffff !important; }
    .ag-sheet {
      box-shadow: none !important;
      margin: 0 !important;
      border: 0 !important;
      padding: 0 !important;
      page-break-after: always;
      break-after: page;
    }
    .ag-sheet:last-child { page-break-after: auto; break-after: auto; }
    .ag-field,
    .ag-field input,
    .ag-field textarea {
      border: 0 !important;
      background: transparent !important;
      padding: 0 !important;
      box-shadow: none !important;
      outline: 0 !important;
      color: #000 !important;
      font-family: inherit !important;
    }
    .ag-field input,
    .ag-field textarea { border-bottom: 1px solid #000 !important; padding-bottom: 1px !important; }
    .ag-field-empty input,
    .ag-field-empty textarea { color: transparent !important; }
    .ag-field-empty::after { content: "________________"; color: #000; }
    .ag-checkbox { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  }
`;

const noop = () => {};

export function AgreementDocument({
  form,
  set = noop,
  setPayment = noop,
  addPayment = noop,
  removePayment = noop,
  readOnly = false,
  sigMode = "template",
  signatures = {},
}) {
  const selectedTier = PRICING_TIERS.find((t) => t.key === form.selectedTier);
  const isCustom = form.selectedTier === "custom";

  return (
    <>
      <style>{PRINT_CSS}</style>
      <div className="ag-stage py-8 sm:py-10 flex flex-col items-center gap-6">
        {/* ─────────── PAGE 1 ─────────── */}
        <Sheet testId="agreement-page-1">
          <Header />
          <H1>Pre-Draft Advisory Services Agreement</H1>
          <div className="text-[11px] text-black/55 mt-1 italic">
            Effective as of{" "}
            <Field value={form.effectiveDate} onChange={(v) => set("effectiveDate", v)} placeholder="MM / DD / YYYY" w="w-40" testId="ag-effective" readOnly={readOnly} />.
          </div>

          <Section title="1. Parties">
            <p>This Pre-Draft Advisory Services Agreement (the "<strong>Agreement</strong>") is entered into by and between <strong>OmniSportsGroup, LLC</strong>, a Tennessee limited liability company, doing business as "<strong>OmniDraftLab</strong>" (the "<strong>Advisor</strong>"), and the family identified below (the "<strong>Family</strong>"):</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Party
                label="The Advisor"
                fields={[
                  { v: "OmniSportsGroup, LLC", static: true, label: "Entity" },
                  { v: "OmniDraftLab", static: true, label: "Doing business as" },
                  { v: "State of Tennessee", static: true, label: "Organized in" },
                  { v: "Brandon A. Taylor", static: true, label: "Principal" },
                ]}
              />
              <Party
                readOnly={readOnly}
                fields={[
                  { v: form.parent1Name, onChange: (x) => set("parent1Name", x), placeholder: "Parent / Guardian 1 — full legal name", label: "Parent / Guardian 1", testId: "ag-parent1" },
                  { v: form.parent2Name, onChange: (x) => set("parent2Name", x), placeholder: "Parent / Guardian 2 — full legal name (optional)", label: "Parent / Guardian 2", testId: "ag-parent2" },
                  { v: form.athleteName, onChange: (x) => set("athleteName", x), placeholder: "Athlete — full legal name", label: "Athlete", testId: "ag-athlete" },
                ]}
                label="The Family"
              />
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <LabeledField label="Position" value={form.athletePosition} onChange={(v) => set("athletePosition", v)} placeholder="e.g. QB" readOnly={readOnly} />
              <LabeledField label="School / Class" value={form.athleteSchool} onChange={(v) => set("athleteSchool", v)} placeholder="e.g. Tennessee · 2026" readOnly={readOnly} />
              <LabeledField label="Family State of Residence" value={form.familyState} onChange={(v) => set("familyState", v)} placeholder="TN" readOnly={readOnly} />
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <LabeledField label="Family Address" value={form.familyAddress} onChange={(v) => set("familyAddress", v)} placeholder="Street" readOnly={readOnly} />
              <LabeledField label="City" value={form.familyCity} onChange={(v) => set("familyCity", v)} placeholder="City" readOnly={readOnly} />
              <LabeledField label="Zip" value={form.familyZip} onChange={(v) => set("familyZip", v)} placeholder="Zip" readOnly={readOnly} />
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <LabeledField label="Email" value={form.familyEmail} onChange={(v) => set("familyEmail", v)} placeholder="family@example.com" readOnly={readOnly} />
              <LabeledField label="Phone" value={form.familyPhone} onChange={(v) => set("familyPhone", v)} placeholder="(615) 555-0100" readOnly={readOnly} />
            </div>
          </Section>

          <Section title="2. Recitals">
            <p>
              The Family wishes to engage the Advisor to provide pre-draft <strong>intelligence, evaluation, preparation, strategic positioning, and family advisory services</strong> in connection with the Athlete's anticipated transition to professional football. The Advisor wishes to provide such services on the terms set forth below. Both parties agree as follows.
            </p>
          </Section>

          <Section title="3. Scope of Services — Engagement Tier">
            <p className="mb-3">The Family selects the following engagement tier (check one):</p>
            <div className="space-y-2.5">
              {PRICING_TIERS.map((t) => (
                <TierOption key={t.key} selected={form.selectedTier === t.key} onSelect={() => !readOnly && set("selectedTier", t.key)} tier={t} testId={`ag-tier-${t.key}`} readOnly={readOnly} />
              ))}
              <CustomTierOption
                selected={isCustom}
                onSelect={() => !readOnly && set("selectedTier", "custom")}
                scope={form.customScope}
                onScopeChange={(v) => set("customScope", v)}
                fee={form.customFee}
                onFeeChange={(v) => set("customFee", v)}
                testId="ag-tier-custom"
                readOnly={readOnly}
              />
            </div>
            <p className="mt-4 text-[11px] italic text-black/60">
              The deliverables associated with the selected tier as published on OmniDraftLab's pricing page on the Effective Date are incorporated by reference and attached as Exhibit A.
            </p>
          </Section>

          <Footer pageOf="1 of 7" />
        </Sheet>

        {/* ─────────── PAGE 2 ─────────── */}
        <Sheet testId="agreement-page-2">
          <Section title="4. Term" first>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <LabeledField label="Engagement begins" value={form.termStart} onChange={(v) => set("termStart", v)} placeholder="MM / DD / YYYY" readOnly={readOnly} />
              <LabeledField label="Engagement ends (or, if blank, upon Athlete's first professional contract)" value={form.termEnd} onChange={(v) => set("termEnd", v)} placeholder="MM / DD / YYYY" readOnly={readOnly} />
            </div>
            <p className="mt-3">Either party may terminate this Agreement in writing at any time pursuant to Section 11. Fees earned through the date of termination remain due and payable.</p>
          </Section>

          <Section title="5. Fees & Payment Schedule">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <LabeledField label="Total engagement fee" value={form.totalFee} onChange={(v) => set("totalFee", v)} placeholder="$" readOnly={readOnly} />
              <div className="text-[11px] text-black/55 italic self-end pb-1">
                Total fee should equal the sum of the installments below (or the custom fee in Section 3 if a custom package is selected).
              </div>
            </div>

            {!readOnly && (
              <div className="mt-4 ag-no-print flex items-center gap-3">
                <div className="text-[11px] uppercase tracking-[0.22em] text-black/55 font-semibold">Payment Schedule (editable)</div>
                <button onClick={addPayment} data-testid="agreement-add-payment" className="ml-auto inline-flex items-center gap-1 text-xs text-rose-700 hover:text-rose-800 font-medium" type="button">
                  <Plus className="h-3 w-3" /> Add installment
                </button>
              </div>
            )}

            <table className="mt-2 w-full border-collapse text-[12.5px]">
              <thead>
                <tr className="border-b-2 border-black/40 text-left text-[10px] uppercase tracking-[0.18em] text-black/60">
                  <th className="py-2 w-10">#</th>
                  <th className="py-2 w-40">Due Date</th>
                  <th className="py-2 w-32">Amount</th>
                  <th className="py-2">Description</th>
                  {!readOnly && <th className="py-2 w-10 ag-no-print"></th>}
                </tr>
              </thead>
              <tbody>
                {form.payments.map((p, i) => (
                  <tr key={i} className="border-b border-black/15" data-testid={`agreement-payment-row-${i}`}>
                    <td className="py-2 align-top text-black/55 font-mono">{String(i + 1).padStart(2, "0")}</td>
                    <td className="py-2 align-top pr-3"><Field value={p.date} onChange={(v) => setPayment(i, "date", v)} placeholder="MM / DD / YYYY" readOnly={readOnly} /></td>
                    <td className="py-2 align-top pr-3"><Field value={p.amount} onChange={(v) => setPayment(i, "amount", v)} placeholder="$" readOnly={readOnly} /></td>
                    <td className="py-2 align-top pr-3"><Field value={p.note} onChange={(v) => setPayment(i, "note", v)} placeholder="Description / milestone" readOnly={readOnly} /></td>
                    {!readOnly && (
                      <td className="py-2 align-top ag-no-print">
                        {form.payments.length > 1 && (
                          <button onClick={() => removePayment(i)} data-testid={`agreement-remove-payment-${i}`} className="text-black/40 hover:text-rose-700" type="button" aria-label="Remove installment">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="mt-4">
              All payments shall be made by wire, ACH, or cashier's check to <strong>OmniSportsGroup, LLC (d/b/a OmniDraftLab)</strong> at the address provided to the Family upon execution. Late payments shall accrue interest at the lesser of one and one-half percent (1.5%) per month or the maximum rate permitted by Tennessee law. Fees are exclusive of out-of-pocket travel and event expenses, which shall be pre-approved in writing and reimbursed at cost.
            </p>
          </Section>

          <Section title="6. Confidentiality">
            <p>
              Each party will treat all non-public information exchanged under this Agreement — including evaluation reports, scouting intelligence, decision matrices, and family communications — as <strong>confidential</strong>, and will not disclose such information to any third party except (a) to professional advisors bound by confidentiality obligations, (b) as compelled by law, or (c) with the disclosing party's prior written consent. This obligation survives termination of the Agreement for a period of five (5) years.
            </p>
          </Section>

          <Footer pageOf="2 of 7" />
        </Sheet>

        {/* ─────────── PAGE 3 ─────────── */}
        <Sheet testId="agreement-page-3">
          <Section title="7. No Guarantee of Outcomes" first>
            <p>
              The Family expressly acknowledges and agrees that the Advisor <strong>does not guarantee</strong>: (a) the Athlete will be selected in the NFL Draft; (b) any particular draft position, round, or team; (c) any post-draft contract, salary, bonus, roster spot, or career outcome; (d) the accuracy, completeness, or actionability of any third-party information referenced in the deliverables. Pre-draft outcomes are determined by 32 independent National Football League franchises whose decisions are outside the Advisor's control.
            </p>
            <p className="mt-3">The Advisor's obligation is to provide the highest level of preparation, intelligence, and strategic guidance available, consistent with industry standards for former front-office personnel.</p>
          </Section>

          <Section title="8. Non-Agent Status; NFLPA / SPARTA / UAAA Acknowledgment">
            <p>
              The Family expressly acknowledges that the Advisor is <strong>not a certified NFLPA Contract Advisor</strong> and is <strong>not acting as an "athlete agent"</strong> within the meaning of the federal Sports Agent Responsibility and Trust Act (15 U.S.C. §§ 7801–7807) or the Tennessee Uniform Athlete Agents Act (Tenn. Code Ann. §§ 49-7-2101 et seq.). The Advisor will <strong>not negotiate</strong> any professional employment contract, endorsement contract, or representation contract on the Athlete's behalf. The Family is solely responsible for retaining separate certified representation for any such negotiations and acknowledges that this Agreement does not establish, replace, or interfere with any such future representation relationship.
            </p>
            <p className="mt-3">Nothing in this Agreement shall be construed as the Advisor recruiting or soliciting the Athlete to enter into a representation agreement with the Advisor or any third party.</p>
          </Section>

          <Section title="9. NCAA / NIL / Eligibility">
            <p>
              If the Athlete is, at the Effective Date, a current student-athlete subject to NCAA rules, the Family and Athlete acknowledge that the receipt of advisory services may have <strong>NIL and amateurism implications</strong>. The Athlete is solely responsible for confirming compliance with all NCAA, conference, and institutional rules and for consulting independent compliance counsel before executing this Agreement. The Advisor disclaims any responsibility for eligibility determinations.
            </p>
          </Section>

          <Section title="10. Independent Contractor; No Other Professional Relationship">
            <p>The Advisor is an independent contractor. This Agreement does not create a legal, fiduciary, medical, accounting, tax, financial-planning, or investment-advisory relationship. The Family and Athlete agree to obtain independent professional advice for any such matters.</p>
          </Section>

          <Section title="11. Termination">
            <p>Either party may terminate this Agreement at any time upon ten (10) days' written notice to the other party. Upon termination: (a) the Advisor will deliver any in-progress work product reasonably capable of being delivered; (b) the Family will pay all fees earned and expenses incurred through the date of termination; (c) Sections 6, 7, 8, 12, 13, 14, and 15 survive termination.</p>
          </Section>

          <Footer pageOf="3 of 7" />
        </Sheet>

        {/* ─────────── PAGE 4 ─────────── */}
        <Sheet testId="agreement-page-4">
          <Section title="12. Limitation of Liability" first>
            <p>To the maximum extent permitted by Tennessee law, the Advisor's aggregate liability arising out of or related to this Agreement, regardless of theory, shall not exceed the <strong>total fees actually paid</strong> by the Family to the Advisor under this Agreement during the twelve (12) months preceding the event giving rise to the claim. Neither party shall be liable for any indirect, incidental, consequential, special, punitive, or exemplary damages.</p>
          </Section>

          <Section title="13. Indemnification">
            <p>The Family agrees to indemnify, defend, and hold harmless the Advisor and its officers, employees, and contractors from and against any and all claims, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of (a) the Family's or Athlete's breach of this Agreement, (b) the Family's or Athlete's misrepresentation or omission of material facts, or (c) any third-party claim arising out of the Athlete's eligibility, amateurism status, or independent representation arrangements.</p>
          </Section>

          <Section title="14. Governing Law; Dispute Resolution">
            <p>This Agreement shall be governed by and construed in accordance with the laws of the <strong>State of Tennessee</strong>, without regard to its conflicts-of-law principles. The parties agree to attempt in good faith to resolve any dispute through informal negotiation for thirty (30) days. Any unresolved dispute shall be submitted to <strong>binding arbitration in Davidson County, Tennessee</strong>, administered by the American Arbitration Association under its Commercial Arbitration Rules. Judgment on the award may be entered in any court of competent jurisdiction. The prevailing party shall be entitled to recover reasonable attorneys' fees and costs.</p>
          </Section>

          <Section title="15. Miscellaneous">
            <p>
              <strong>Entire Agreement.</strong> This Agreement, together with Exhibit A, constitutes the entire agreement between the parties and supersedes all prior discussions, proposals, and agreements.
              <br /><strong>Amendments.</strong> Any modification must be in a writing signed by both parties.
              <br /><strong>Notices.</strong> Written notices shall be delivered by email to the addresses listed in Section 1, with a courtesy copy by certified mail.
              <br /><strong>Severability.</strong> If any provision is held unenforceable, the remainder of the Agreement remains in effect.
              <br /><strong>Counterparts; Electronic Signature.</strong> This Agreement may be executed in counterparts and by electronic signature, each of which is deemed an original.
              <br /><strong>No Assignment.</strong> Neither party may assign this Agreement without the other party's prior written consent.
            </p>
          </Section>

          <Footer pageOf="4 of 7" />
        </Sheet>

        {/* ─────────── PAGE 5 ─────────── */}
        <Sheet testId="agreement-page-5">
          <Section title="16. Advisor Intellectual Property" first>
            <p>All reports, evaluations, scouting assessments, grading systems, development plans, interview preparation materials, strategic recommendations, databases, templates, methodologies, and other materials created or provided by Advisor remain the exclusive intellectual property of <strong>OmniSportsGroup, LLC (d/b/a OmniDraftLab)</strong>.</p>
            <p className="mt-3">The Family and Athlete are granted a limited, non-exclusive, non-transferable license to use such materials solely for their personal evaluation and professional development purposes. The Family and Athlete may not reproduce, distribute, sell, publish, sublicense, or commercially exploit any Advisor materials without prior written consent.</p>
          </Section>

          <Section title="17. Refund Policy">
            <p>Except as otherwise expressly stated in this Agreement, <strong>all fees paid under this Agreement are non-refundable</strong>. Upon termination, the Family shall remain responsible for all fees earned and expenses incurred through the effective date of termination.</p>
          </Section>

          <Section title="18. No Legal, Tax, or Financial Advice">
            <p>The Advisor is <strong>not a law firm, accounting firm, financial advisory firm, or licensed sports agency</strong>. Any discussion regarding contracts, NIL opportunities, endorsements, representation agreements, tax matters, business structures, or financial decisions is provided solely for educational and strategic purposes.</p>
            <p className="mt-3">The Family and Athlete are encouraged to seek independent legal, accounting, financial, and professional advice before making any material business or contractual decisions.</p>
          </Section>

          <Footer pageOf="5 of 7" />
        </Sheet>

        {/* ─────────── PAGE 6 ─────────── */}
        <Sheet testId="agreement-page-6">
          <Section title="19. Publicity and Use of Name, Image, and Likeness" first>
            <p>Advisor shall <strong>not</strong> use the Athlete's name, image, likeness, testimonial, scouting information, or confidential personal information for advertising, promotional, or marketing purposes without the <strong>prior written consent</strong> of the Athlete or Family.</p>
            <p className="mt-3">Nothing in this section prevents Advisor from maintaining confidential internal business records relating to services performed under this Agreement.</p>
          </Section>

          <Section title="20. Non-Circumvention">
            <p>During the term of this Agreement and for <strong>twelve (12) months following termination</strong>, the Family and Athlete agree not to intentionally circumvent Advisor by directly engaging professional service providers, consultants, trainers, media professionals, or other third parties introduced through Advisor for the purpose of avoiding compensation otherwise due to Advisor.</p>
            <p className="mt-3">Nothing in this section shall prohibit the Family or Athlete from independently engaging any service provider not introduced by Advisor.</p>
          </Section>

          <Section title="21. Nature of Services">
            <p>The Advisor provides independent pre-draft intelligence, evaluation, preparation, strategic guidance, and family advisory services. Advisor <strong>does not</strong> provide athlete representation, contract negotiation, legal services, recruiting services, financial planning services, or employment placement services.</p>
          </Section>

          <Footer pageOf="6 of 7" />
        </Sheet>

        {/* ─────────── PAGE 7 · Signatures ─────────── */}
        <Sheet testId="agreement-page-7">
          <div>
            <H2>Signatures</H2>
            <p className="text-[11.5px] text-black/65 mt-1">By signing below, each party acknowledges that they have read, understood, and agreed to be bound by the terms of this Agreement, and that they have had a reasonable opportunity to consult independent counsel.</p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-7">
              <SignatureBlock role="The Advisor" printedName="Brandon A. Taylor" printedTitle="Founder & Managing Member, OmniSportsGroup, LLC" testIdPrefix="ag-sig-advisor" sigMode={sigMode} signature={signatures.advisor} readOnly={readOnly} />
              <SignatureBlock role="Parent / Guardian 1" printedName={form.parent1Name} onPrintedNameChange={(v) => set("parent1Name", v)} testIdPrefix="ag-sig-parent1" sigMode={sigMode} signature={signatures.parent1} readOnly={readOnly} />
              <SignatureBlock role="Parent / Guardian 2" printedName={form.parent2Name} onPrintedNameChange={(v) => set("parent2Name", v)} testIdPrefix="ag-sig-parent2" optional sigMode={sigMode} signature={signatures.parent2} readOnly={readOnly} />
              <SignatureBlock role="Athlete (acknowledgment of advisory engagement)" printedName={form.athleteName} onPrintedNameChange={(v) => set("athleteName", v)} testIdPrefix="ag-sig-athlete" sigMode={sigMode} signature={signatures.athlete} readOnly={readOnly} />
            </div>
          </div>

          <Footer pageOf="7 of 7" />
        </Sheet>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function Sheet({ children, testId }) {
  return (
    <div className="ag-sheet bg-white text-[#0b0d11] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.25)] ring-1 ring-black/5 px-[0.7in] py-[0.6in]" style={{ width: "8.5in", minHeight: "11in", fontFamily: "Georgia, 'Times New Roman', serif" }} data-testid={testId}>
      {children}
    </div>
  );
}

function Header() {
  return (
    <div className="flex items-center justify-between border-b border-black/15 pb-3 mb-4">
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-lg grid place-items-center bg-[#04111A]"><Hexagon className="h-4 w-4 text-cyan-300" strokeWidth={2.4} /></div>
        <div>
          <div className="text-[14px] font-semibold tracking-tight leading-none" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>OmniDraftLab</div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-black/55 mt-1" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>Powered by OmniSportsGroup, LLC</div>
        </div>
      </div>
      <div className="text-right">
        <div className="text-[9px] uppercase tracking-[0.22em] text-black/55" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>State of Tennessee</div>
        <div className="text-[10px] font-medium text-black/65 mt-0.5" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>Advisory Services Agreement</div>
      </div>
    </div>
  );
}

function Footer({ pageOf }) {
  return (
    <div className="ag-sheet-footer mt-6 pt-3 border-t border-black/10 flex items-center justify-between text-[9.5px] text-black/55" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div>© OmniSportsGroup, LLC (d/b/a OmniDraftLab) · Pre-Draft Advisory Services Agreement · Tennessee</div>
      <div className="font-mono uppercase tracking-[0.2em]">Page {pageOf}</div>
    </div>
  );
}

function H1({ children }) {
  return <h1 className="text-[26px] font-semibold tracking-tight leading-tight mt-3" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>{children}</h1>;
}
function H2({ children }) {
  return <h2 className="text-[17px] font-semibold tracking-tight" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>{children}</h2>;
}

function Section({ title, first, children }) {
  return (
    <section className={cn(first ? "" : "mt-5")}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-rose-700 font-semibold mb-1.5" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>{title.split(".")[0]}.</div>
      <h3 className="text-[15px] font-semibold tracking-tight mb-2" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>{title.split(".").slice(1).join(".").trim()}</h3>
      <div className="text-[12.5px] leading-[1.55] text-black/85 whitespace-pre-line">{children}</div>
    </section>
  );
}

function Field({ value, onChange, placeholder, w = "w-48", type = "text", testId, readOnly }) {
  const empty = !value;
  return (
    <span className={cn("ag-field inline-block", empty && "ag-field-empty")}>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        readOnly={readOnly}
        className={cn("bg-transparent border-b border-black/30 focus:border-rose-600 focus:outline-none text-[12.5px] py-0.5 px-1 disabled:text-black", readOnly && "read-only:text-black/90 cursor-default", w)}
        style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
      />
    </span>
  );
}

function LabeledField({ label, value, onChange, placeholder, multiline, readOnly }) {
  return (
    <label className="block">
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-black/55 font-semibold mb-1" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>{label}</div>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={2} readOnly={readOnly} className="w-full border-b border-black/30 focus:border-rose-600 focus:outline-none text-[12.5px] py-0.5 px-1 bg-transparent read-only:text-black/90" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} readOnly={readOnly} className="w-full border-b border-black/30 focus:border-rose-600 focus:outline-none text-[12.5px] py-0.5 px-1 bg-transparent read-only:text-black/90" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }} />
      )}
    </label>
  );
}

function Party({ label, fields, readOnly }) {
  return (
    <div className="rounded-lg border border-black/15 p-4 bg-black/[0.015]">
      <div className="text-[9.5px] uppercase tracking-[0.22em] text-rose-700 font-semibold" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>{label}</div>
      <div className="mt-2 space-y-2">
        {fields.map((f, i) => (
          <div key={i}>
            <div className="text-[9.5px] uppercase tracking-[0.18em] text-black/50 mb-0.5" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>{f.label}</div>
            {f.static ? (
              <div className="text-[13px] font-medium" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>{f.v}</div>
            ) : (
              <input type="text" value={f.v} onChange={(e) => f.onChange(e.target.value)} placeholder={f.placeholder} data-testid={f.testId} readOnly={readOnly} className="w-full border-b border-black/30 focus:border-rose-600 focus:outline-none text-[13px] py-0.5 bg-transparent read-only:text-black/90" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TierOption({ selected, onSelect, tier, testId, readOnly }) {
  return (
    <label onClick={onSelect} data-testid={testId} className={cn("flex items-start gap-3 p-3 rounded-lg border transition", readOnly ? "cursor-default" : "cursor-pointer", selected ? "border-rose-600 bg-rose-50" : "border-black/15 hover:bg-black/[0.02]")}>
      <span className={cn("ag-checkbox h-4 w-4 rounded-sm border-2 shrink-0 mt-0.5 grid place-items-center text-white", selected ? "bg-rose-600 border-rose-600" : "border-black/40")}>
        {selected && <span className="text-[10px] leading-none">✓</span>}
      </span>
      <div className="flex-1 min-w-0" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-[13px] font-semibold">{tier.name}</div>
          <div className="text-[12px] font-mono tabular-nums">{tier.price}{tier.priceRange ? ` ${tier.priceRange}` : ""} · <span className="uppercase tracking-[0.16em] text-black/55 text-[10px]">{tier.cadence}</span></div>
        </div>
        <div className="text-[11px] text-black/65 italic mt-0.5">{tier.tagline}</div>
      </div>
    </label>
  );
}

function CustomTierOption({ selected, onSelect, scope, onScopeChange, fee, onFeeChange, testId, readOnly }) {
  return (
    <div data-testid={testId} className={cn("p-3 rounded-lg border transition", selected ? "border-rose-600 bg-rose-50" : "border-black/15")}>
      <label onClick={onSelect} className={cn("flex items-start gap-3", readOnly ? "cursor-default" : "cursor-pointer")}>
        <span className={cn("ag-checkbox h-4 w-4 rounded-sm border-2 shrink-0 mt-0.5 grid place-items-center text-white", selected ? "bg-rose-600 border-rose-600" : "border-black/40")}>
          {selected && <span className="text-[10px] leading-none">✓</span>}
        </span>
        <div className="flex-1 min-w-0" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-[13px] font-semibold">Custom Package</div>
            <div className="text-[12px] font-mono tabular-nums uppercase tracking-[0.16em] text-black/55">See custom fee below</div>
          </div>
          <div className="text-[11px] text-black/65 italic mt-0.5">A bespoke scope and fee negotiated between the parties.</div>
        </div>
      </label>
      {selected && (
        <div className="mt-3 pl-7 grid grid-cols-1 gap-3" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
          <label className="block">
            <div className="text-[9.5px] uppercase tracking-[0.2em] text-black/55 font-semibold mb-1">Custom scope of services</div>
            <textarea value={scope} onChange={(e) => onScopeChange(e.target.value)} placeholder="Describe the specific services to be provided, deliverables, milestones, and any exclusions…" rows={3} readOnly={readOnly} className="w-full border-b border-black/30 focus:border-rose-600 focus:outline-none text-[12.5px] py-1 px-1 bg-transparent read-only:text-black/90" />
          </label>
          <label className="block">
            <div className="text-[9.5px] uppercase tracking-[0.2em] text-black/55 font-semibold mb-1">Custom total fee</div>
            <input type="text" value={fee} onChange={(e) => onFeeChange(e.target.value)} placeholder="$" readOnly={readOnly} className="w-48 border-b border-black/30 focus:border-rose-600 focus:outline-none text-[12.5px] py-0.5 px-1 bg-transparent read-only:text-black/90" />
          </label>
        </div>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

function SignatureBlock({ role, printedName, onPrintedNameChange, printedTitle, optional, testIdPrefix, sigMode, signature, readOnly }) {
  const labelFont = { fontFamily: "system-ui, -apple-system, sans-serif" };

  // Contract mode: render the executed e-signature stamp, or an awaiting state.
  if (sigMode === "contract") {
    const signed = !!signature?.signed_at;
    return (
      <div data-testid={testIdPrefix}>
        <div className="text-[10px] uppercase tracking-[0.22em] text-rose-700 font-semibold" style={labelFont}>
          {role} {optional && <span className="text-black/40 normal-case font-normal italic">(if applicable)</span>}
        </div>
        {signed ? (
          <>
            <div className="mt-3 border-b border-black/50 h-10 flex items-end pb-1" data-testid={`${testIdPrefix}-signed`}>
              <span className="text-[22px] leading-none text-black" style={{ fontFamily: "'Segoe Script', 'Brush Script MT', cursive" }}>{signature.name}</span>
            </div>
            <div className="mt-1 grid grid-cols-2 gap-2 text-[10px] uppercase tracking-[0.18em] text-black/55" style={labelFont}>
              <div>Signature</div>
              <div className="text-right">{fmtDate(signature.signed_at)}</div>
            </div>
            <div className="mt-2 text-[9.5px] text-emerald-700 font-semibold uppercase tracking-[0.16em]" style={labelFont}>✓ Signed electronically</div>
          </>
        ) : (
          <>
            <div className="mt-4 border-b border-black/50 h-10" />
            <div className="mt-1 grid grid-cols-2 gap-2 text-[10px] uppercase tracking-[0.18em] text-black/55" style={labelFont}>
              <div>Signature</div>
              <div className="text-right">Date</div>
            </div>
            <div className="mt-2 text-[9.5px] text-black/45 italic" style={labelFont}>Awaiting signature</div>
          </>
        )}
        <div className="mt-2" style={labelFont}>
          <div className="text-[9.5px] uppercase tracking-[0.2em] text-black/55 font-semibold mb-1">Printed name</div>
          <div className="text-[13px] font-medium border-b border-black/30 pb-1 min-h-[20px]">{signature?.name || printedName}</div>
          {printedTitle && (
            <div className="mt-2">
              <div className="text-[9.5px] uppercase tracking-[0.2em] text-black/55 font-semibold mb-1">Title</div>
              <div className="text-[12.5px] border-b border-black/30 pb-1">{printedTitle}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Template mode: blank signature line + editable printed name (public page).
  return (
    <div data-testid={testIdPrefix}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-rose-700 font-semibold" style={labelFont}>
        {role} {optional && <span className="text-black/40 normal-case font-normal italic">(if applicable)</span>}
      </div>
      <div className="mt-4 border-b border-black/50 h-10" />
      <div className="mt-1 grid grid-cols-2 gap-2 text-[10px] uppercase tracking-[0.18em] text-black/55" style={labelFont}>
        <div>Signature</div>
        <div className="text-right">Date</div>
      </div>
      <div className="mt-3" style={labelFont}>
        <div className="text-[9.5px] uppercase tracking-[0.2em] text-black/55 font-semibold mb-1">Printed name</div>
        {onPrintedNameChange && !readOnly ? (
          <input type="text" value={printedName} onChange={(e) => onPrintedNameChange(e.target.value)} placeholder="Full legal name" data-testid={`${testIdPrefix}-name`} className="w-full border-b border-black/30 focus:border-rose-600 focus:outline-none text-[12.5px] py-0.5 bg-transparent" />
        ) : (
          <div className="text-[13px] font-medium border-b border-black/30 pb-1">{printedName}</div>
        )}
        {printedTitle && (
          <div className="mt-2">
            <div className="text-[9.5px] uppercase tracking-[0.2em] text-black/55 font-semibold mb-1">Title</div>
            <div className="text-[12.5px] border-b border-black/30 pb-1">{printedTitle}</div>
          </div>
        )}
      </div>
    </div>
  );
}
